// src/api/salonApi.js - MIGRATED VERSION for Flat Hierarchy (salons collection)

import { 
  doc, 
  getDoc, 
  setDoc, 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  writeBatch, 
  increment,
  Timestamp,
  limit,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../utils/firebase';

const API_BASE_URL = process.env.API_BASE_URL || 'http://localhost:5000';

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

// ==================== SALON CONFIGURATION - MIGRATED TO FLAT HIERARCHY ====================

/**
 * Get salon configuration - Uses 'salons' collection
 */
export const getSalonConfig = async (salonId) => {
  try {
    if (!salonId) {
      throw new Error('Salon ID is required');
    }

    console.log("Fetching salon config for:", salonId);

    const salonRef = doc(db, 'salons', salonId);
    const salonDoc = await getDoc(salonRef);

    if (salonDoc.exists()) {
      console.log("✓ Found in 'salons' collection");
      const data = salonDoc.data();

      // Convert flat hierarchy to legacy format for backward compatibility
      const legacyFormat = {
        salonId: data.salonId || salonId,
        branding: data.branding || {},
        contact: {
          address: data.address || '',
          phone: data.phone || '',
          email: data.email || '',
          socialMedia: data.socialMedia || {}
        },
        businessHours: data.businessHours || {},
        aboutUs: data.aboutUs || '',
        createdAt: data.createdAt,
        updatedAt: data.updatedAt
      };

      // Ensure aboutUs is a string
      if (Array.isArray(legacyFormat.aboutUs)) {
        legacyFormat.aboutUs = legacyFormat.aboutUs.join(' ');
      }

      return legacyFormat;
    }

    console.log("✗ Salon not found");
    return null;

  } catch (error) {
    console.error('Error fetching salon config:', error);
    throw error;
  }
};

/**
 * Update salon configuration - Saves to 'salons' collection
 */
export const updateSalonConfig = async (salonId, configData) => {
  try {
    if (!salonId) {
      throw new Error('Salon ID is required');
    }

    console.log("Saving configuration to 'salons' collection for:", salonId);

    // Clean and prepare data
    const cleanData = JSON.parse(JSON.stringify(configData));

    // Ensure aboutUs is stored as string
    if (Array.isArray(cleanData.aboutUs)) {
      cleanData.aboutUs = cleanData.aboutUs.join(' ');
    }
    if (cleanData.aboutUs !== undefined && typeof cleanData.aboutUs !== 'string') {
      cleanData.aboutUs = String(cleanData.aboutUs);
    }

    const salonRef = doc(db, 'salons', salonId);
    const salonDoc = await getDoc(salonRef);

    if (!salonDoc.exists()) {
      // Create new salon document with flat structure
      const newSalonData = {
        clientId: cleanData.clientId || 'demo-client',
        salonId: salonId,
        name: cleanData.branding?.name || 'Unknown Salon',
        subdomain: salonId,
        customDomain: null,

        // Extract contact info from nested structure
        address: cleanData.contact?.address || '',
        phone: cleanData.contact?.phone || '',
        email: cleanData.contact?.email || '',

        // Settings
        settings: cleanData.settings || {
          timezone: 'Asia/Singapore',
          currency: 'SGD',
          language: 'en'
        },

        // Keep branding as nested object
        branding: cleanData.branding || {},

        // Extract social media
        socialMedia: cleanData.contact?.socialMedia || {},

        // Business hours and content
        businessHours: cleanData.businessHours || {},
        aboutUs: cleanData.aboutUs || '',

        status: 'active',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      await setDoc(salonRef, newSalonData);
      console.log("✓ Created new salon in 'salons' collection");
    } else {
      // Update existing salon - convert to flat hierarchy
      const existingSalonData = salonDoc.data();
      const updateData = {
        // Update root-level contact fields
        address: cleanData.contact?.address || existingSalonData.address,
        phone: cleanData.contact?.phone || existingSalonData.phone,
        email: cleanData.contact?.email || existingSalonData.email,

        // Update nested objects
        branding: cleanData.branding || existingSalonData.branding,
        socialMedia: cleanData.contact?.socialMedia || existingSalonData.socialMedia,
        businessHours: cleanData.businessHours || existingSalonData.businessHours,
        aboutUs: cleanData.aboutUs || existingSalonData.aboutUs,

        updatedAt: serverTimestamp()
      };

      // Cascade branding.name to all dependent name fields in the salons doc
      if (cleanData.branding?.name) {
        const newName = cleanData.branding.name;
        updateData.name = newName;
        if (existingSalonData.paynowConfig) {
          updateData['paynowConfig.salonName'] = newName;
        }
        if (existingSalonData.whatsappBranding) {
          updateData['whatsappBranding.displayName'] = newName;
          updateData['whatsappBranding.businessName'] = newName;
        }
        if (existingSalonData.loyaltyProgram) {
          updateData['loyaltyProgram.name'] = newName;
        }
      }

      await updateDoc(salonRef, updateData);
      console.log("✓ Updated salon in 'salons' collection");

      // Cascade branding.name to the clients collection
      if (cleanData.branding?.name) {
        try {
          const clientsSnap = await getDocs(
            query(collection(db, 'clients'), where('salonId', '==', salonId))
          );
          for (const clientDoc of clientsSnap.docs) {
            await updateDoc(clientDoc.ref, {
              name: cleanData.branding.name,
              updatedAt: serverTimestamp()
            });
          }
          if (!clientsSnap.empty) {
            console.log('✓ Cascaded name to clients collection for salonId:', salonId);
          }
        } catch (clientError) {
          console.error('Error cascading name to clients collection:', clientError);
        }
      }
    }

    return true;
  } catch (error) {
    console.error('Error updating salon config:', error);
    throw error;
  }
};

/**
 * Upload image asset (unchanged - uses external API)
 */
export const uploadImage = async (salonId, file, type) => {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('type', type);

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/salons/${salonId}/upload`, 
      {
        method: 'POST',
        body: formData
      }
    );
    const data = await response.json();
    return data.url;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

/**
 * Delete image asset (unchanged - uses external API)
 */
export const deleteImage = async (salonId, imageUrl) => {
  try {
    await fetch(`${API_BASE_URL}/api/salons/${salonId}/images`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ imageUrl })
    });
    return true;
  } catch (error) {
    console.error('Error deleting image:', error);
    throw error;
  }
};

// ==================== SERVICES MANAGEMENT - MIGRATED TO FLAT HIERARCHY ====================

/**
 * Get salon services - Uses flat 'services' collection
 * Client-side filtering to avoid composite index requirements
 */
export const getSalonServices = async (salonId) => {
  try {
    if (!salonId) {
      throw new Error('Salon ID is required');
    }

    console.log('Fetching services for salon:', salonId);

    const servicesRef = collection(db, 'services');
    const snapshot = await getDocs(servicesRef);

    const servicesList = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      // Client-side filtering by salonId
      if (data.salonId === salonId) {
        // `id` may be overridden by a stored `id` field (seeded services keep a UUID there that
        // appointments/transactions reference), so expose the real Firestore doc ID as `docId`
        // for writes.
        servicesList.push({
          id: doc.id,
          ...data,
          docId: doc.id
        });
      }
    });

    console.log(`✓ Found ${servicesList.length} services`);
    return servicesList;
  } catch (error) {
    console.error('Error fetching salon services:', error);
    return [];
  }
};

/**
 * Get salon staff - Uses flat 'staff' collection
 */
export const getSalonStaff = async (salonId) => {
  try {
    if (!salonId) {
      throw new Error('Salon ID is required');
    }

    console.log('Fetching staff for salon:', salonId);

    const staffRef = collection(db, 'staff');
    const snapshot = await getDocs(staffRef);

    const staffList = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      // Client-side filtering by salonId
      if (data.salonId === salonId) {
        staffList.push({
          id: doc.id,
          ...data
        });
      }
    });

    console.log(`✓ Found ${staffList.length} staff`);
    return staffList;
  } catch (error) {
    console.error('Error fetching salon staff:', error);
    return [];
  }
};

/**
 * Add a new service - Saves to flat 'services' collection
 */
export const addSalonService = async (salonId, serviceData) => {
  try {
    if (!salonId) {
      throw new Error('Salon ID is required');
    }

    console.log('Adding service for salon:', salonId);

    const servicesRef = collection(db, 'services');
    const newService = {
      clientId: 'demo-client',
      salonId: salonId,
      ...serviceData,
      serviceId: serviceData.serviceId || generateUUID(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    const serviceRef = await addDoc(servicesRef, newService);
    console.log(`✓ Service added: ${serviceRef.id}`);

    return serviceRef.id;
  } catch (error) {
    console.error('Error adding service:', error);
    throw error;
  }
};

/**
 * Update an existing service - Uses flat 'services' collection
 */
export const updateSalonService = async (salonId, serviceId, serviceData) => {
  try {
    if (!salonId || !serviceId) {
      throw new Error('Salon ID and Service ID are required');
    }

    console.log('Updating service:', serviceId);

    const serviceRef = doc(db, 'services', serviceId);
    await updateDoc(serviceRef, {
      ...serviceData,
      updatedAt: serverTimestamp()
    });
    console.log('✓ Updated service');
    return true;
  } catch (error) {
    console.error('Error updating service:', error);
    throw error;
  }
};

/**
 * Delete a service - Uses flat 'services' collection
 */
export const deleteSalonService = async (salonId, serviceId) => {
  try {
    if (!salonId || !serviceId) {
      throw new Error('Salon ID and Service ID are required');
    }

    console.log('Deleting service:', serviceId);

    const serviceRef = doc(db, 'services', serviceId);
    await deleteDoc(serviceRef);
    console.log('✓ Deleted service');
    return true;
  } catch (error) {
    console.error('Error deleting service:', error);
    throw error;
  }
};

// ==================== ALL OTHER FUNCTIONS REMAIN UNCHANGED ====================
// The following functions are preserved exactly as they were in your original file

/**
 * Get customer's appointment history from subcollection
 */
export const getCustomerAppointments = async (customerId) => {
  try {
    if (!customerId) {
      throw new Error('Customer ID is required');
    }

    console.log('Fetching appointments for customer:', customerId);
    
    const appointmentsRef = collection(db, 'customers', customerId, 'appointments');
    const q = query(appointmentsRef, orderBy('dateTimeCreated', 'desc'));
    const querySnapshot = await getDocs(q);
    
    const appointments = [];
    querySnapshot.forEach(doc => {
      const data = doc.data();
      appointments.push({
        id: doc.id,
        ...data,
        dateTimeRequested: data.dateTimeRequested?.seconds ? 
          new Date(data.dateTimeRequested.seconds * 1000) : 
          data.dateTimeRequested,
        dateTimeCreated: data.dateTimeCreated?.seconds ? 
          new Date(data.dateTimeCreated.seconds * 1000) : 
          data.dateTimeCreated,
        updatedAt: data.updatedAt?.seconds ? 
          new Date(data.updatedAt.seconds * 1000) : 
          data.updatedAt
      });
    });
    
    console.log(`Found ${appointments.length} appointments`);
    return appointments;
  } catch (error) {
    console.error('Error getting customer appointments:', error);
    throw error;
  }
};

/**
 * Generate PayNow QR code for payment (placeholder)
 */
export const generatePayNowQR = async (transactionId, amount, additionalInfo = {}) => {
  try {
    console.log('Generating PayNow QR code for transaction:', transactionId, 'Amount:', amount);
    
    const qrData = {
      transactionId,
      amount,
      timestamp: new Date().toISOString(),
      qrCode: `PAYNOW_${transactionId}_${amount}_${Date.now()}`,
      expiresAt: new Date(Date.now() + 15 * 60 * 1000),
      ...additionalInfo
    };
    
    console.log('PayNow QR code generated:', qrData);
    return qrData;
  } catch (error) {
    console.error('Error generating PayNow QR:', error);
    throw error;
  }
};

/**
 * Update customer loyalty metrics after transaction
 */
export const updateCustomerLoyaltyAfterTransaction = async (customerId, transactionData, salonId) => {
  try {
    if (!customerId || !transactionData) {
      throw new Error('Customer ID and transaction data are required');
    }

    // Fetch loyalty program settings from salon config
    let pointsPerCurrency = 10;
    let currencyPerPoint = 10;
    if (salonId) {
      const salonRef = doc(db, 'salons', salonId);
      const salonDoc = await getDoc(salonRef);
      if (salonDoc.exists()) {
        const loyaltyProgram = salonDoc.data().loyaltyProgram || {};
        pointsPerCurrency = loyaltyProgram.pointsPerCurrency || 10;
        currencyPerPoint = loyaltyProgram.currencyPerPoint || 10;
      }
    }

    const price = transactionData.price || 0;
    const pointsEarned = Math.floor((price / currencyPerPoint) * pointsPerCurrency);

    const customerRef = doc(db, 'customers', customerId);
    await updateDoc(customerRef, {
      'metrics.totalSpent': increment(price),
      'metrics.pointsEarned': increment(pointsEarned),
      'metrics.pointsBalance': increment(pointsEarned),
      'metrics.lastTransaction': Timestamp.fromDate(new Date()),
      updatedAt: Timestamp.fromDate(new Date())
    });

    console.log(`Updated customer loyalty metrics: +${pointsEarned} points (${pointsPerCurrency} per $${currencyPerPoint})`);
    return true;
  } catch (error) {
    console.error('Error updating customer loyalty metrics:', error);
    throw error;
  }
};

// ==================== PROMOTIONS & CUSTOMER MANAGEMENT ====================
// (All remaining functions preserved unchanged from original file)

export const getSalonPromotions = async (salonId) => {
  try {
    if (!salonId) throw new Error('Salon ID is required');
    
    const promotionsRef = collection(db, 'promotions');
    const q = query(promotionsRef, where('salonId', '==', salonId));
    const snapshot = await getDocs(q);
    
    const promotions = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    return promotions;
  } catch (error) {
    console.error('Error getting promotions:', error);
    return [];
  }
};

export const addSalonPromotion = async (salonId, promotionData) => {
  try {
    if (!salonId) throw new Error('Salon ID is required');
    
    const promotionsRef = collection(db, 'promotions');
    const docRef = await addDoc(promotionsRef, {
      ...promotionData,
      salonId,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding promotion:', error);
    throw error;
  }
};

export const updateSalonPromotion = async (salonId, promotionId, promotionData) => {
  try {
    if (!salonId || !promotionId) {
      throw new Error('Salon ID and Promotion ID are required');
    }

    const promotionRef = doc(db, 'promotions', promotionId);
    await updateDoc(promotionRef, {
      ...promotionData,
      updatedAt: new Date()
    });

    // Cascade relevant changes to all linked vouchers
    await cascadePromotionChangesToVouchers(salonId, promotionId, promotionData);

    return true;
  } catch (error) {
    console.error('Error updating promotion:', error);
    throw error;
  }
};

export const deleteSalonPromotion = async (salonId, promotionId) => {
  try {
    if (!salonId || !promotionId) {
      throw new Error('Salon ID and Promotion ID are required');
    }

    // Mark all linked vouchers as inactive before deleting the promotion
    await cascadePromotionChangesToVouchers(salonId, promotionId, { isActive: false });

    const promotionRef = doc(db, 'promotions', promotionId);
    await deleteDoc(promotionRef);

    return true;
  } catch (error) {
    console.error('Error deleting promotion:', error);
    throw error;
  }
};

/**
 * Cascade promotion field changes to all linked vouchers in both
 * the main 'vouchers' collection and each customer's sub-collection.
 */
export const cascadePromotionChangesToVouchers = async (salonId, promotionId, changedFields) => {
  try {
    if (!salonId || !promotionId) {
      throw new Error('Salon ID and Promotion ID are required');
    }

    // Map promotion fields to their voucher counterparts
    const FIELD_MAP = {
      name: 'promotionName',
      description: 'description',
      discountValue: 'discountValue',
      voucherCode: 'voucherCode',
      type: 'type',
      freeGiftDescription: 'freeGiftDescription',
      pointsRequired: 'pointsRequired',
      voucherAmount: 'voucherAmount',
      startDate: 'startDate',
      endDate: 'endDate'
    };

    // Build the voucher update payload from changed promotion fields
    const voucherUpdate = { updatedAt: serverTimestamp() };
    let hasRelevantChanges = false;

    for (const [promoField, voucherField] of Object.entries(FIELD_MAP)) {
      if (changedFields.hasOwnProperty(promoField)) {
        let value = changedFields[promoField];
        if (value instanceof Date) {
          value = Timestamp.fromDate(value);
        }
        voucherUpdate[voucherField] = value;
        hasRelevantChanges = true;
      }
    }

    // Track whether isActive changed (for status derivation)
    const isActiveChanged = changedFields.hasOwnProperty('isActive');
    if (isActiveChanged) hasRelevantChanges = true;

    if (!hasRelevantChanges) {
      return { updated: 0 };
    }

    // Query all vouchers for this promotion in the main collection
    const vouchersRef = collection(db, 'vouchers');
    const vouchersQuery = query(
      vouchersRef,
      where('promotionId', '==', promotionId),
      where('salonId', '==', salonId)
    );
    const vouchersSnapshot = await getDocs(vouchersQuery);

    if (vouchersSnapshot.empty) {
      return { updated: 0 };
    }

    const MAX_BATCH = 450;
    let batch = writeBatch(db);
    let batchCount = 0;
    let updatedCount = 0;

    // Update main collection vouchers
    for (const voucherDoc of vouchersSnapshot.docs) {
      const voucherData = voucherDoc.data();
      const thisUpdate = { ...voucherUpdate };

      // Derive voucher status from isActive — never touch redeemed vouchers
      if (isActiveChanged && voucherData.status !== 'redeemed') {
        if (changedFields.isActive === false && voucherData.status === 'active') {
          thisUpdate.status = 'inactive';
        } else if (changedFields.isActive === true && voucherData.status === 'inactive') {
          thisUpdate.status = 'active';
        }
      }

      batch.update(doc(db, 'vouchers', voucherDoc.id), thisUpdate);
      batchCount++;
      updatedCount++;

      if (batchCount >= MAX_BATCH) {
        await batch.commit();
        batch = writeBatch(db);
        batchCount = 0;
      }
    }

    if (batchCount > 0) {
      await batch.commit();
    }

    console.log(`Cascaded promotion changes to ${updatedCount} vouchers`);
    return { updated: updatedCount };
  } catch (error) {
    console.error('Error cascading promotion changes to vouchers:', error);
    throw error;
  }
};

export const getCustomerTargetingPreview = async (salonId, targetingOptions) => {
  try {
    if (!salonId) throw new Error('Salon ID is required');

    const customersRef = collection(db, 'customers');
    const q = query(customersRef, where('salonId', '==', salonId));
    const snapshot = await getDocs(q);

    const allCustomers = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    let eligibleCustomers = allCustomers;

    if (targetingOptions.targetingType === 'customer_profile') {
      eligibleCustomers = allCustomers.filter(customer => {
        // Check birth months filter (AND condition - must match if specified)
        if (targetingOptions.targetingCriteria.birthMonths &&
            targetingOptions.targetingCriteria.birthMonths.length > 0) {
          const customerBirthMonth = customer.birthMonth;
          if (!customerBirthMonth || !targetingOptions.targetingCriteria.birthMonths.includes(customerBirthMonth)) {
            return false;
          }
        }

        // Check postal codes filter (AND condition - must match if specified)
        if (targetingOptions.targetingCriteria.postalCodes &&
            targetingOptions.targetingCriteria.postalCodes.length > 0) {
          const customerPostal = customer.postalCode || '';
          // Check if customer's postal code starts with any of the specified codes
          const matchesPostal = targetingOptions.targetingCriteria.postalCodes.some(code =>
            customerPostal.startsWith(code)
          );
          if (!matchesPostal) {
            return false;
          }
        }

        // Customer passes all specified criteria (AND logic)
        return true;
      });
    }

    // Handle customer_transaction targeting type
    if (targetingOptions.targetingType === 'customer_transaction') {
      eligibleCustomers = allCustomers.filter(customer => {
        const totalVisits = customer.metrics?.totalVisits || 0;
        const totalSpent = customer.metrics?.totalSpent || 0;
        const lastTransaction = customer.metrics?.lastTransaction || customer.lastVisit;

        // Check transaction count criteria (AND condition)
        const transactionCount = targetingOptions.targetingCriteria.transactionCount;
        const transactionType = targetingOptions.targetingCriteria.transactionType;

        if (transactionType && transactionType !== 'not_selected') {
          if (transactionType === 'less_than' && totalVisits >= transactionCount) {
            return false;
          }
          if (transactionType === 'equal_to' && totalVisits !== transactionCount) {
            return false;
          }
          if (transactionType === 'greater_than' && totalVisits <= transactionCount) {
            return false;
          }
        }

        // Check transaction amount criteria (AND condition)
        const transactionAmount = targetingOptions.targetingCriteria.transactionAmount;
        const transactionAmountType = targetingOptions.targetingCriteria.transactionAmountType;

        if (transactionAmountType && transactionAmountType !== 'not_selected') {
          if (transactionAmountType === 'greater_than' && totalSpent <= transactionAmount) {
            return false;
          }
          if (transactionAmountType === 'less_than' && totalSpent >= transactionAmount) {
            return false;
          }
        }

        // Check last transaction time criteria (AND condition)
        const timeValue = targetingOptions.targetingCriteria.lastTransactionMonths;
        const timeUnit = targetingOptions.targetingCriteria.lastTransactionUnit || 'months';

        if (timeValue && timeValue > 0) {
          if (!lastTransaction) {
            return false; // No transaction record, exclude
          }

          // Convert lastTransaction to Date
          let lastTransactionDate;
          if (lastTransaction.toDate) {
            lastTransactionDate = lastTransaction.toDate();
          } else if (lastTransaction.seconds) {
            lastTransactionDate = new Date(lastTransaction.seconds * 1000);
          } else {
            lastTransactionDate = new Date(lastTransaction);
          }

          // Calculate cutoff date
          const cutoffDate = new Date();
          switch (timeUnit) {
            case 'hours':
              cutoffDate.setHours(cutoffDate.getHours() - timeValue);
              break;
            case 'days':
              cutoffDate.setDate(cutoffDate.getDate() - timeValue);
              break;
            case 'years':
              cutoffDate.setFullYear(cutoffDate.getFullYear() - timeValue);
              break;
            default: // months
              cutoffDate.setMonth(cutoffDate.getMonth() - timeValue);
          }

          // Customer's last transaction must be within the time window
          if (lastTransactionDate < cutoffDate) {
            return false;
          }
        }

        // Customer passes all specified criteria (AND logic)
        return true;
      });
    }

    // Handle customer_points targeting type
    if (targetingOptions.targetingType === 'customer_points') {
      eligibleCustomers = allCustomers.filter(customer => {
        // Check member tier criteria (AND condition)
        const memberTiers = targetingOptions.targetingCriteria.memberTiers;
        if (memberTiers && memberTiers.length > 0) {
          const customerTier = customer.loyaltyTier || customer.memberTier || '';
          if (!memberTiers.includes(customerTier)) {
            return false;
          }
        }

        // Check points criteria (AND condition)
        const pointsBalance = customer.metrics?.pointsBalance || customer.loyaltyPoints || 0;
        const pointsAmount = targetingOptions.targetingCriteria.pointsAmount;
        const pointsType = targetingOptions.targetingCriteria.pointsType;

        if (pointsType && pointsType !== 'not_selected') {
          if (pointsType === 'greater_than' && pointsBalance <= pointsAmount) {
            return false;
          }
          if (pointsType === 'less_than' && pointsBalance >= pointsAmount) {
            return false;
          }
        }

        // Customer passes all specified criteria (AND logic)
        return true;
      });
    }

    // Handle customer_appointment targeting type
    if (targetingOptions.targetingType === 'customer_appointment') {
      // Query appointments collection
      const appointmentsRef = collection(db, 'appointments');
      const appointmentsQuery = query(appointmentsRef, where('salonId', '==', salonId));
      const appointmentsSnapshot = await getDocs(appointmentsQuery);

      const appointments = appointmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Fetch services and staff for ID-to-name lookups
      const servicesRef = collection(db, 'services');
      const servicesQuery = query(servicesRef, where('salonId', '==', salonId));
      const servicesSnapshot = await getDocs(servicesQuery);
      const servicesMap = new Map();
      servicesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        servicesMap.set(doc.id, data.name);
      });

      const staffRef = collection(db, 'staff');
      const staffQuery = query(staffRef, where('salonId', '==', salonId));
      const staffSnapshot = await getDocs(staffQuery);
      const staffMap = new Map();
      staffSnapshot.docs.forEach(doc => {
        const data = doc.data();
        staffMap.set(doc.id, data.name);
      });

      // Calculate time window for upcoming appointments
      const now = new Date();
      const cutoffDate = new Date();
      const timeValue = targetingOptions.targetingCriteria.appointmentTimeValue || 0;
      const timeUnit = targetingOptions.targetingCriteria.appointmentTimeUnit || 'days';

      if (timeValue > 0) {
        switch (timeUnit) {
          case 'hours':
            cutoffDate.setHours(cutoffDate.getHours() + timeValue);
            break;
          case 'days':
            cutoffDate.setDate(cutoffDate.getDate() + timeValue);
            break;
          case 'months':
            cutoffDate.setMonth(cutoffDate.getMonth() + timeValue);
            break;
          case 'years':
            cutoffDate.setFullYear(cutoffDate.getFullYear() + timeValue);
            break;
          default:
            cutoffDate.setDate(cutoffDate.getDate() + timeValue);
        }
      }

      // Filter appointments based on criteria
      const matchingAppointments = appointments.filter(appointment => {
        // Check time window
        if (timeValue > 0) {
          let appointmentDate;
          // Try dateTimeRequested first, then fall back to date
          const dateField = appointment.dateTimeRequested || appointment.date;
          if (dateField?.toDate) {
            appointmentDate = dateField.toDate();
          } else if (dateField?.seconds) {
            appointmentDate = new Date(dateField.seconds * 1000);
          } else if (dateField) {
            appointmentDate = new Date(dateField);
          } else {
            return false;
          }

          // Appointment must be in the future and within the time window
          if (appointmentDate < now || appointmentDate > cutoffDate) {
            return false;
          }
        }

        // Check service criteria - compare by name using ID lookup
        const serviceFilterId = targetingOptions.targetingCriteria.appointmentService;
        if (serviceFilterId && serviceFilterId !== 'not_selected' && serviceFilterId !== '') {
          const serviceFilterName = servicesMap.get(serviceFilterId);
          const appointmentServiceName = appointment.serviceRequested?.name || appointment.serviceName || appointment.service;
          if (serviceFilterName && appointmentServiceName) {
            if (serviceFilterName.toLowerCase() !== appointmentServiceName.toLowerCase()) {
              return false;
            }
          } else {
            return false;
          }
        }

        // Check staff criteria - compare by name using ID lookup
        const staffFilterId = targetingOptions.targetingCriteria.appointmentStaff;
        if (staffFilterId && staffFilterId !== 'not_selected' && staffFilterId !== '') {
          const staffFilterName = staffMap.get(staffFilterId);
          const appointmentStaffName = appointment.staffName || appointment.staff;
          if (staffFilterName && appointmentStaffName) {
            if (staffFilterName.toLowerCase() !== appointmentStaffName.toLowerCase()) {
              return false;
            }
          } else {
            return false;
          }
        }

        // Check status criteria - handle both string and array
        const statusFilter = targetingOptions.targetingCriteria.appointmentStatus;
        if (statusFilter && statusFilter.length > 0) {
          // statusFilter can be an array (multi-select) or a string
          const statusArray = Array.isArray(statusFilter) ? statusFilter : [statusFilter];
          if (!statusArray.includes(appointment.status)) {
            return false;
          }
        }

        return true;
      });

      // Get unique customer IDs from matching appointments
      const customerIdsWithAppointments = new Set();
      matchingAppointments.forEach(apt => {
        if (apt.customerId) {
          customerIdsWithAppointments.add(apt.customerId);
        }
        if (apt.customerEmail) {
          // Find customer by email
          const matchingCustomer = allCustomers.find(c => c.email === apt.customerEmail);
          if (matchingCustomer) {
            customerIdsWithAppointments.add(matchingCustomer.id);
          }
        }
      });

      // Filter customers who have matching appointments
      eligibleCustomers = allCustomers.filter(customer =>
        customerIdsWithAppointments.has(customer.id)
      );
    }

    // Handle specific_customer targeting type
    if (targetingOptions.targetingType === 'specific_customer') {
      const specificCustomers = targetingOptions.targetingCriteria.specificCustomers || [];
      if (specificCustomers.length > 0) {
        // Extract IDs and emails from the customer objects
        const specificCustomerIds = specificCustomers.map(c => c.id);
        const specificCustomerEmails = specificCustomers.map(c => c.email);
        eligibleCustomers = allCustomers.filter(customer =>
          specificCustomerIds.includes(customer.id) || specificCustomerEmails.includes(customer.email)
        );
      }
    }

    // Handle customer_notes targeting type — #Revised UI_v2
    if (targetingOptions.targetingType === 'customer_notes') {
      const searchText = (targetingOptions.targetingCriteria.notesSearchText || '').trim().toLowerCase();
      if (searchText) {
        const matchType = targetingOptions.targetingCriteria.notesMatchType || 'contains_all';
        eligibleCustomers = allCustomers.filter(customer => {
          const notes = (customer.customer_notes || '').toLowerCase();
          if (!notes) return false;
          if (matchType === 'exact') return notes.includes(searchText);
          const words = searchText.split(/[\s,]+/).filter(w => w.length > 0);
          if (matchType === 'contains_any') return words.some(word => notes.includes(word));
          return words.every(word => notes.includes(word)); // contains_all
        });
      }
    }

    // Handle all_customers targeting type (no filtering needed)
    // eligibleCustomers already contains all customers

    return {
      totalCount: allCustomers.length,
      eligibleCount: eligibleCustomers.length,
      eligibleCustomers: eligibleCustomers.map(c => ({
        id: c.id,
        email: c.email,
        displayName: c.displayName,
        phone: c.phone || c.phoneNumber || c.mobile || '',
        postalCode: c.postalCode || '',
        birthMonth: c.birthMonth || '',
        totalVisits: c.metrics?.totalVisits || 0,
        totalSpent: c.metrics?.totalSpent || 0,
        lastTransaction: c.metrics?.lastTransaction || c.lastVisit || null,
        loyaltyTier: c.loyaltyTier || c.memberTier || '',
        pointsBalance: c.metrics?.pointsBalance || c.loyaltyPoints || 0,
        locationId: c.locationId || '',
        preferences: c.preferences || {}
      }))
    };
  } catch (error) {
    console.error('Error getting customer targeting preview:', error);
    throw error;
  }
};

/**
 * Push vouchers to specific customers from preview list.
 * Creates records in both the main 'vouchers' collection and each customer's subcollection.
 * Skips customers who already have a voucher for this promotion.
 */
export const pushVouchersToCustomers = async (salonId, promotionId, promotionData, eligibleCustomers) => {
  try {
    if (!salonId || !promotionId) {
      throw new Error('Salon ID and Promotion ID are required');
    }
    if (!eligibleCustomers || eligibleCustomers.length === 0) {
      throw new Error('No eligible customers to push vouchers to');
    }

    console.log(`Pushing vouchers for promotion ${promotionId} to ${eligibleCustomers.length} customers`);

    // Build set of newly eligible customer emails
    const eligibleEmails = new Set(
      eligibleCustomers.map(c => c.email)
    );

    // Check for existing vouchers for this promotion
    const existingVouchersRef = collection(db, 'vouchers');
    const existingQuery = query(
      existingVouchersRef,
      where('promotionId', '==', promotionId),
      where('salonId', '==', salonId)
    );
    const existingSnapshot = await getDocs(existingQuery);
    const existingCustomerEmails = new Set();

    // Identify vouchers that belong to customers no longer eligible
    const vouchersToRemove = [];
    existingSnapshot.forEach(docSnap => {
      const data = docSnap.data();
      if (data.customerEmail) {
        existingCustomerEmails.add(data.customerEmail);
        if (!eligibleEmails.has(data.customerEmail) && data.status !== 'redeemed') {
          vouchersToRemove.push({
            id: docSnap.id,
            customerId: data.customerId,
            customerEmail: data.customerEmail
          });
        }
      }
    });

    // Remove vouchers for customers who no longer match targeting criteria
    if (vouchersToRemove.length > 0) {
      console.log(`Removing ${vouchersToRemove.length} vouchers for customers no longer matching criteria`);

      let removeBatch = writeBatch(db);
      let removeBatchCount = 0;
      const MAX_BATCH_REMOVE = 450;

      for (const v of vouchersToRemove) {
        // Delete from main vouchers collection
        removeBatch.delete(doc(db, 'vouchers', v.id));
        removeBatchCount++;

        if (removeBatchCount >= MAX_BATCH_REMOVE) {
          await removeBatch.commit();
          removeBatch = writeBatch(db);
          removeBatchCount = 0;
        }
      }

      if (removeBatchCount > 0) {
        await removeBatch.commit();
      }

      console.log(`Successfully removed ${vouchersToRemove.length} outdated vouchers`);
    }

    // Filter out customers who already have a voucher for this promotion
    const newCustomers = eligibleCustomers.filter(
      customer => !existingCustomerEmails.has(customer.email)
    );

    if (newCustomers.length === 0 && vouchersToRemove.length === 0) {
      console.log('All customers already have vouchers for this promotion');
      return { created: 0, skipped: eligibleCustomers.length, removed: 0 };
    }

    if (newCustomers.length === 0) {
      // Only removals happened, update the distributed count
      const promotionRef = doc(db, 'promotions', promotionId);
      const promotionDoc = await getDoc(promotionRef);
      const currentDistributed = promotionDoc.exists() ? (promotionDoc.data().vouchersDistributed || 0) : 0;
      const newDistributed = Math.max(0, currentDistributed - vouchersToRemove.length);
      await updateDoc(promotionRef, {
        vouchersDistributed: newDistributed,
        updatedAt: serverTimestamp()
      });
      return { created: 0, skipped: eligibleCustomers.length, removed: vouchersToRemove.length };
    }

    console.log(`Creating vouchers for ${newCustomers.length} new customers (${eligibleCustomers.length - newCustomers.length} skipped, ${vouchersToRemove.length} removed)`);

    let batch = writeBatch(db);
    let batchCount = 0;
    const MAX_BATCH = 450; // Firestore batch limit is 500, leave room for extra writes

    const voucherBase = {
      salonId,
      promotionId,
      promotionName: promotionData.name || '',
      description: promotionData.description || '',
      voucherCode: promotionData.voucherCode || '',
      type: promotionData.type || '',
      discountValue: promotionData.discountValue || 0,
      freeGiftDescription: promotionData.freeGiftDescription || '',
      pointsRequired: promotionData.pointsRequired || 0,
      voucherAmount: promotionData.voucherAmount || 0,
      startDate: promotionData.startDate instanceof Date ? Timestamp.fromDate(promotionData.startDate) : promotionData.startDate,
      endDate: promotionData.endDate instanceof Date ? Timestamp.fromDate(promotionData.endDate) : promotionData.endDate,
      status: 'active',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    for (const customer of newCustomers) {
      if (batchCount >= MAX_BATCH) {
        // Commit current batch and start a new one
        await batch.commit();
        batch = writeBatch(db);
        batchCount = 0;
      }

      const customerId = customer.id || `${customer.email}_${salonId}`;
      const customerEmail = customer.email;

      const voucherData = {
        ...voucherBase,
        customerId,
        customerEmail
      };

      // Write to main 'vouchers' collection
      const mainVoucherRef = doc(collection(db, 'vouchers'));
      batch.set(mainVoucherRef, voucherData);
      batchCount++;
    }

    // Commit remaining
    if (batchCount > 0) {
      await batch.commit();
    }

    // Update promotion's vouchersDistributed count (add new, subtract removed)
    const promotionRef = doc(db, 'promotions', promotionId);
    const promotionDoc = await getDoc(promotionRef);
    const currentDistributed = promotionDoc.exists() ? (promotionDoc.data().vouchersDistributed || 0) : 0;
    const newDistributed = Math.max(0, currentDistributed + newCustomers.length - vouchersToRemove.length);
    await updateDoc(promotionRef, {
      vouchersDistributed: newDistributed,
      updatedAt: serverTimestamp()
    });

    console.log(`Successfully pushed ${newCustomers.length} vouchers, removed ${vouchersToRemove.length}`);
    return { created: newCustomers.length, skipped: eligibleCustomers.length - newCustomers.length, removed: vouchersToRemove.length };
  } catch (error) {
    console.error('Error pushing vouchers to customers:', error);
    throw error;
  }
};

export const assignVouchersForNewCustomer = async (salonId, customerId, customerEmail) => {
  try {
    if (!salonId || !customerId || !customerEmail) return;

    // Find promotions targeting all customers that have already been distributed
    const promotionsRef = collection(db, 'promotions');
    const q = query(
      promotionsRef,
      where('salonId', '==', salonId),
      where('targetingType', '==', 'all_customers')
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) return;

    const now = new Date();
    let batch = writeBatch(db);
    let batchCount = 0;
    const MAX_BATCH = 450;
    let assigned = 0;

    for (const promoDoc of snapshot.docs) {
      const promo = promoDoc.data();

      // Skip promotions that haven't had vouchers pushed yet
      if (!promo.vouchersDistributed || promo.vouchersDistributed <= 0) continue;

      // Skip expired promotions
      const endDate = promo.endDate?.seconds
        ? new Date(promo.endDate.seconds * 1000)
        : promo.endDate instanceof Date ? promo.endDate : null;
      if (endDate && endDate < now) continue;

      // Check if this customer already has a voucher for this promotion
      const existingQuery = query(
        collection(db, 'vouchers'),
        where('promotionId', '==', promoDoc.id),
        where('customerEmail', '==', customerEmail),
        where('salonId', '==', salonId)
      );
      const existingSnap = await getDocs(existingQuery);
      if (!existingSnap.empty) continue;

      const startDate = promo.startDate?.seconds
        ? Timestamp.fromMillis(promo.startDate.seconds * 1000)
        : promo.startDate;
      const endTimestamp = promo.endDate?.seconds
        ? Timestamp.fromMillis(promo.endDate.seconds * 1000)
        : promo.endDate;

      const voucherData = {
        salonId,
        promotionId: promoDoc.id,
        promotionName: promo.name || '',
        description: promo.description || '',
        voucherCode: promo.voucherCode || '',
        type: promo.type || '',
        discountValue: promo.discountValue || 0,
        freeGiftDescription: promo.freeGiftDescription || '',
        pointsRequired: promo.pointsRequired || 0,
        voucherAmount: promo.voucherAmount || 0,
        startDate: startDate,
        endDate: endTimestamp,
        status: 'active',
        customerId,
        customerEmail,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Write to main vouchers collection
      const mainRef = doc(collection(db, 'vouchers'));
      batch.set(mainRef, voucherData);
      batchCount++;

      // Update promotion distributed count
      batch.update(doc(db, 'promotions', promoDoc.id), {
        vouchersDistributed: increment(1),
        updatedAt: serverTimestamp()
      });
      batchCount++;

      assigned++;

      if (batchCount >= MAX_BATCH) {
        await batch.commit();
        batch = writeBatch(db);
        batchCount = 0;
      }
    }

    if (batchCount > 0) {
      await batch.commit();
    }

    if (assigned > 0) {
      console.log(`Auto-assigned ${assigned} voucher(s) to new customer ${customerEmail}`);
    }
  } catch (error) {
    // Non-blocking: log error but don't prevent customer signup
    console.error('Error auto-assigning vouchers to new customer:', error);
  }
};

export const getSalonCustomers = async (salonId, options = {}) => {
  try {
    if (!salonId) throw new Error('Salon ID is required');
    
    const customersRef = collection(db, 'customers');
    let q = query(customersRef, where('salonId', '==', salonId));
    
    if (options.limit) {
      q = query(q, limit(options.limit));
    }
    
    const querySnapshot = await getDocs(q);
    
    const customers = [];
    querySnapshot.forEach(doc => {
      const data = doc.data();
      customers.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.seconds ? new Date(data.createdAt.seconds * 1000) : data.createdAt,
        updatedAt: data.updatedAt?.seconds ? new Date(data.updatedAt.seconds * 1000) : data.updatedAt,
        lastLoginAt: data.lastLoginAt?.seconds ? new Date(data.lastLoginAt.seconds * 1000) : data.lastLoginAt
      });
    });
    
    return customers;
  } catch (error) {
    console.error('Error getting salon customers:', error);
    throw error;
  }
};

export const searchCustomers = async (salonId, searchTerm) => {
  try {
    if (!salonId || !searchTerm) {
      throw new Error('Salon ID and search term are required');
    }
    
    const allCustomers = await getSalonCustomers(salonId);
    
    const searchLower = searchTerm.toLowerCase();
    const filteredCustomers = allCustomers.filter(customer => {
      const displayName = (customer.displayName || '').toLowerCase();
      const email = (customer.email || '').toLowerCase();
      
      return displayName.includes(searchLower) || email.includes(searchLower);
    });
    
    return filteredCustomers;
  } catch (error) {
    console.error('Error searching customers:', error);
    throw error;
  }
};

export const getCustomerEligibleVouchers = async (salonId, customerEmail) => {
  try {
    if (!salonId || !customerEmail) {
      throw new Error('Salon ID and customer email are required');
    }
    
    const voucherHistory = await getCustomerVoucherHistory(salonId, customerEmail);
    const eligiblePromotions = await getCustomerEligiblePromotions(salonId, customerEmail);
    
    return {
      voucherHistory,
      eligiblePromotions
    };
  } catch (error) {
    console.error('Error getting customer eligible vouchers:', error);
    throw error;
  }
};

export const getCustomerStatistics = async (salonId) => {
  try {
    if (!salonId) throw new Error('Salon ID is required');
    
    const customers = await getSalonCustomers(salonId);
    
    const totalCustomers = customers.length;
    const totalRevenue = customers.reduce((sum, customer) => 
      sum + (customer.metrics?.totalSpent || 0), 0
    );
    const totalPoints = customers.reduce((sum, customer) => 
      sum + (customer.metrics?.pointsBalance || 0), 0
    );
    
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const recentCustomers = customers.filter(customer => {
      const createdDate = customer.createdAt?.seconds ? 
        new Date(customer.createdAt.seconds * 1000) : 
        customer.createdAt;
      return createdDate && createdDate >= thirtyDaysAgo;
    });
    
    const statistics = {
      totalCustomers,
      recentCustomers: recentCustomers.length,
      totalRevenue,
      averageSpending: totalCustomers > 0 ? totalRevenue / totalCustomers : 0,
      totalPointsInCirculation: totalPoints,
      topSpenders: customers
        .sort((a, b) => (b.metrics?.totalSpent || 0) - (a.metrics?.totalSpent || 0))
        .slice(0, 5)
    };
    
    return statistics;
  } catch (error) {
    console.error('Error getting customer statistics:', error);
    throw error;
  }
};

export const getCustomerVoucherHistory = async (salonId, customerEmail) => {
  try {
    if (!salonId || !customerEmail) {
      throw new Error('Salon ID and Customer Email are required');
    }
    
    const vouchersRef = collection(db, 'vouchers');
    const q = query(
      vouchersRef,
      where('customerEmail', '==', customerEmail),
      where('salonId', '==', salonId)
    );
    const snapshot = await getDocs(q);
    
    const vouchers = [];
    snapshot.forEach(doc => {
      vouchers.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return vouchers;
  } catch (error) {
    console.error('Error getting customer voucher history:', error);
    throw error;
  }
};

/**
 * Redeem a voucher: marks it as redeemed and increments the promotion's
 * currentRedemptions counter. Called after a transaction is saved successfully.
 */
export const redeemVoucher = async (voucherId, transactionId, promotionId) => {
  try {
    const batch = writeBatch(db);

    // Mark the voucher as redeemed
    const voucherRef = doc(db, 'vouchers', voucherId);
    batch.update(voucherRef, {
      status: 'redeemed',
      redeemedAt: serverTimestamp(),
      redeemedTransactionId: transactionId,
      updatedAt: serverTimestamp()
    });

    // Increment the promotion's redemption counter (only if promotionId provided)
    if (promotionId) {
      const promotionRef = doc(db, 'promotions', promotionId);
      batch.update(promotionRef, {
        currentRedemptions: increment(1),
        updatedAt: serverTimestamp()
      });
    }

    await batch.commit();
  } catch (error) {
    console.error('Error redeeming voucher:', error);
    throw error;
  }
};

export const getCustomerEligiblePromotions = async (salonId, customerEmail) => {
  try {
    if (!salonId || !customerEmail) {
      throw new Error('Salon ID and Customer Email are required');
    }
    
    const customerDocRef = doc(db, 'customers', `${customerEmail}_${salonId}`);
    const customerDoc = await getDoc(customerDocRef);
    
    if (!customerDoc.exists()) {
      throw new Error('Customer not found');
    }
    
    const promotionsRef = collection(db, 'promotions');
    const promotionsQuery = query(
      promotionsRef,
      where('salonId', '==', salonId),
      where('isActive', '==', true)
    );
    const promotionsSnapshot = await getDocs(promotionsQuery);
    
    const allPromotions = [];
    promotionsSnapshot.forEach(doc => {
      allPromotions.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return allPromotions;
  } catch (error) {
    console.error('Error getting customer eligible promotions:', error);
    throw error;
  }
};

// ==================== NOTIFICATION FUNCTIONS - USING scheduledCampaigns ====================

export const getSalonNotifications = async (salonId) => {
  try {
    const notificationsRef = collection(db, 'scheduledCampaigns');
    const q = query(
      notificationsRef, 
      where('salonId', '==', salonId),
      where('metadata.campaignType', '==', 'actual_blast')
    );
    
    const querySnapshot = await getDocs(q);
    const notifications = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      notifications.push({
        id: doc.id,
        ...data,
        subject: data.subject || data.title,
        displayType: data.type === 'push' ? 'Push Notification' : 'Email Campaign',
        scheduledDateTime: data.scheduledDate && data.scheduledTime ? 
          new Date(`${data.scheduledDate}T${data.scheduledTime}`) : 
          data.createdAt?.toDate ? data.createdAt.toDate() : new Date()
      });
    });
    
    notifications.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      return dateB - dateA;
    });
    
    return notifications;
  } catch (error) {
    console.error('Error fetching notifications:', error);
    throw error;
  }
};

export const addSalonNotification = async (salonId, notificationData) => {
  try {
    const notificationsRef = collection(db, 'scheduledCampaigns');
    const docRef = await addDoc(notificationsRef, {
      ...notificationData,
      salonId,
      createdAt: new Date(),
      updatedAt: new Date(),
      metadata: {
        ...notificationData.metadata,
        campaignType: 'actual_blast'
      }
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding notification:', error);
    throw error;
  }
};

export const updateSalonNotification = async (salonId, notificationId, updateData) => {
  try {
    const notificationRef = doc(db, 'scheduledCampaigns', notificationId);
    await updateDoc(notificationRef, {
      ...updateData,
      updatedAt: new Date()
    });
    
    return true;
  } catch (error) {
    console.error('Error updating notification:', error);
    throw error;
  }
};

export const deleteSalonNotification = async (salonId, notificationId) => {
  try {
    const notificationRef = doc(db, 'scheduledCampaigns', notificationId);
    await deleteDoc(notificationRef);
    
    return true;
  } catch (error) {
    console.error('Error deleting notification:', error);
    throw error;
  }
};

export const handleEmailUnsubscribe = async (customerId, salonId) => {
  try {
    const customerRef = doc(db, 'customers', customerId);
    await updateDoc(customerRef, {
      'preferences.notifications.email': false,
      unsubscribedAt: new Date(),
      updatedAt: new Date()
    });
    
    return { success: true };
  } catch (error) {
    console.error('Error handling email unsubscribe:', error);
    throw error;
  }
};

export const handlePushUnsubscribe = async (customerId, salonId) => {
  try {
    const customerRef = doc(db, 'customers', customerId);
    await updateDoc(customerRef, {
      'preferences.notifications.push': false,
      pushUnsubscribedAt: new Date(),
      updatedAt: new Date()
    });

    return { success: true };
  } catch (error) {
    console.error('Error handling push unsubscribe:', error);
    throw error;
  }
};

// ==================== BACKWARD COMPATIBILITY EXPORTS ====================
// Maintain old function names for backward compatibility during migration
export const getSalonStylists = getSalonStaff;